import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { Map as ImmutableMap, RecordOf } from 'immutable';
import { memo } from 'react';
import type React from 'react';

import type { AccountProps } from 'app-components/flux/accounts/accountsTypes';
import type { CashFlowTransaction, SplitProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';
import QAmountField from 'components/QAmountField';
import QPanelButton from 'components/QPanelButton';
import AccountField from 'components/QuickenControls/AccountField';
import { OtherSideField } from 'components/Transactions/OtherSideField';

import { useStyles } from '../styles';

type Props = {
  accountId: string;
  allAccounts: ImmutableMap<string, AccountProps>;
  amount: number;
  fullDisableQBP: boolean;
  goToOtherSide: (txn: CashFlowTransaction) => void;
  hasSplitSeries: boolean;
  hasSplitTransferCounterpart: boolean;
  isAssetPayment: boolean;
  isIncome: boolean;
  isManual: boolean;
  isMatched: boolean;
  linkedExpense: boolean;
  openAccounts: ImmutableMap<string, AccountProps>;
  openSplitDialog: () => void;
  selectAccount: (account: AccountProps | undefined) => void;
  showMatchOrTransferRow: boolean;
  showPaymentDialogLink: boolean;
  split: RecordOf<SplitProps> | null;
  supportedTransfer: boolean | null | undefined;
  txn: CashFlowTransaction;
  unMatchTransaction: (txn: CashFlowTransaction) => void;
  updateAmount: (event: { target: { value: string } }) => void;
};

function AmountAccountRow({
  accountId,
  allAccounts,
  amount,
  fullDisableQBP,
  goToOtherSide,
  hasSplitSeries,
  hasSplitTransferCounterpart,
  isAssetPayment,
  isIncome,
  isManual,
  isMatched,
  linkedExpense,
  openAccounts,
  openSplitDialog,
  selectAccount,
  showMatchOrTransferRow,
  showPaymentDialogLink,
  split,
  supportedTransfer,
  txn,
  unMatchTransaction,
  updateAmount,
}: Props): React.JSX.Element {
  const { classes } = useStyles();
  const showSplitAmount = Boolean((split?.items || hasSplitSeries) && !hasSplitTransferCounterpart);

  return (
    <div id='amount-account-row'>
      <div className={classes.thinRow}>
        {showSplitAmount ? (
          <QPanelButton
            id='edit-split-amount'
            disabled={linkedExpense || (txn.type === 'PAYMENT' && isAssetPayment) || txn.subtype === 'SALES_RECEIPT'}
            className={classes.editSplitAmount}
            onClick={() => {
              openSplitDialog();
              tracker.track(tracker.events.viewSplitsV2, {
                initiator: txn.id === null ? 'createTxn' : 'editTxnWithSplits',
                transactionAmount: amount,
                transactionAmountSign: amount >= 0 ? 'positive' : 'negative',
              });
            }}
            title={
              <QAmountField
                label='Amount'
                value={amount}
                showSign='positive'
                showColor='positive'
                inputId='details-amount'
              />
            }
            arrow={
              <IconButton size='small'>
                <ChevronRightIcon fontSize='small' />
              </IconButton>
            }
          />
        ) : (
          <QAmountField
            label='Amount'
            margin='none'
            className={classes.column}
            textFieldVariant='filled'
            editable
            submitOnBlur
            value={amount}
            onChange={updateAmount}
            onSubmit={updateAmount}
            expectedSign={isIncome ? 'positive' : 'negative'}
            id='details-amount'
            disabled={fullDisableQBP || hasSplitTransferCounterpart}
          />
        )}

        <AccountField
          initialAccountId={accountId}
          accounts={showPaymentDialogLink || txn.type === 'PAYMENT' ? allAccounts : openAccounts}
          onSelected={selectAccount}
          label='Account'
          variant='filled'
          margin='none'
          className={classes.column}
          readOnly={!isManual || hasSplitTransferCounterpart}
          id='account-select'
          disabled={fullDisableQBP || hasSplitTransferCounterpart}
        />
        {txn.paymentTxnType ? (
          <TextField id='payment-type-field' label='Type' variant='filled' value={txn.paymentTxnType} disabled />
        ) : null}
      </div>

      {showMatchOrTransferRow && (
        <div className={classes.refundLine}>
          <Typography variant='captionRegular'>
            {isMatched ? (
              <>
                {txn.source === 'USER_ENTERED'
                  ? 'A manual and bank transaction were combined to create this entry. '
                  : 'A pending and cleared transaction were combined to create this entry. '}
                <Link
                  className={classes.link}
                  onClick={() => {
                    unMatchTransaction(txn);
                  }}
                  id='unmerge-button'
                >
                  Unmerge
                </Link>
                <br />
              </>
            ) : null}
            {supportedTransfer ? (
              <OtherSideField
                coa={txn.coa}
                transferId={txn.transfer?.id}
                txnId={txn.id ?? txn.clientId}
                goToOtherSide={goToOtherSide}
              />
            ) : null}
          </Typography>
        </div>
      )}
    </div>
  );
}

export default memo(AmountAccountRow);
